import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Hospital, FlaskConical, Pill, Ambulance, Users, KeyRound, Mail, RefreshCcw, UserPlus,
  ShieldCheck, ShieldX, Copy, Search,
} from "lucide-react";

const KIND_META = {
  hospital:  { icon: Hospital,      color: "text-red-700",     bg: "bg-red-500/15",     portal: "/partner" },
  lab:       { icon: FlaskConical,  color: "text-emerald-700", bg: "bg-emerald-500/15", portal: "/partner" },
  pharmacy:  { icon: Pill,          color: "text-amber-700",   bg: "bg-amber-500/15",   portal: "/partner" },
  ambulance: { icon: Ambulance,     color: "text-red-700",     bg: "bg-red-600/15",     portal: "/driver"  },
};

const STATUS_COLOR = {
  approved: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-900",
  suspended: "bg-slate-200 text-slate-800",
  rejected: "bg-red-100 text-red-800",
};

export default function AdminPartners() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [invite, setInvite] = useState(null);   // {row} when dialog open
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [credsDialog, setCredsDialog] = useState(null); // {email, temp_password, portal_url, kind}

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/partner-directory"); setRows(data); }
    catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (tab === "with_login" && !r.partner_user) return false;
      if (tab === "no_login" && r.partner_user) return false;
      if (tab !== "all" && tab !== "with_login" && tab !== "no_login" && r.kind !== tab) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return (r.facility_name || "").toLowerCase().includes(s)
          || (r.partner_user?.email || "").toLowerCase().includes(s)
          || (r.city || "").toLowerCase().includes(s);
    });
  }, [rows, tab, q]);

  const openInvite = (r) => {
    setInvite(r);
    setForm({ name: "", email: "", phone: "" });
  };

  const submitInvite = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/partner-invite", {
        kind: invite.kind, facility_id: invite.facility_id,
        name: form.name, email: form.email, phone: form.phone,
      });
      setInvite(null);
      setCredsDialog({ email: data.user.email, temp_password: data.temp_password,
                       portal_url: data.portal_url, kind: invite.kind });
      toast.success(`Invite sent to ${data.user.email}`);
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const resend = async (r) => {
    try {
      const { data } = await api.post(`/admin/partner-invite/${r.partner_user.id}/resend`);
      toast.success(`Reminder emailed to ${data.email}`);
    } catch (e) { toast.error(formatError(e)); }
  };

  const reset = async (r) => {
    if (!window.confirm(`Reset password for ${r.partner_user.email}? A new one-time password will be emailed.`)) return;
    try {
      const { data } = await api.post(`/admin/partner-invite/${r.partner_user.id}/reset-password`);
      setCredsDialog({ email: data.email, temp_password: data.temp_password,
                       portal_url: data.portal_url, kind: r.kind });
      toast.success(`Password reset for ${data.email}`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const setStatus = async (r, status) => {
    try {
      await api.post(`/admin/partners/${r.partner_user.id}/status`, { approval_status: status });
      toast.success(`${r.partner_user.email} → ${status}`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  const counts = useMemo(() => {
    const total = rows.length;
    const withLogin = rows.filter((r) => r.partner_user).length;
    return { total, withLogin, missing: total - withLogin };
  }, [rows]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-24 sm:pb-8">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-teal-500/15 text-teal-700 flex items-center justify-center">
            <Users className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-teal-700">Admin · Partner Access</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold">Who can sign in to which facility?</h1>
            <p className="text-slate-600 text-sm">Everyone uses the same <code className="text-teal-700">/login</code> URL. Partners land on <code>/partner</code>, drivers on <code>/driver</code>.</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <StatChip label="Facilities" value={counts.total} />
          <StatChip label="With login" value={counts.withLogin} tone="emerald" />
          <StatChip label="No login" value={counts.missing} tone="amber" />
          <Button variant="outline" size="sm" onClick={load} data-testid="partners-refresh"><RefreshCcw className="w-4 h-4 mr-2" /> Refresh</Button>
        </div>
      </div>

      <Card className="p-3 sm:p-4 rounded-2xl mb-4 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[220px]">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="partner-search" placeholder="Search by facility, city or partner email…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="with_login" data-testid="tab-with">With login</TabsTrigger>
            <TabsTrigger value="no_login" data-testid="tab-no">Missing</TabsTrigger>
            <TabsTrigger value="hospital">Hospitals</TabsTrigger>
            <TabsTrigger value="lab">Labs</TabsTrigger>
            <TabsTrigger value="pharmacy">Pharmacies</TabsTrigger>
            <TabsTrigger value="ambulance">Ambulances</TabsTrigger>
          </TabsList>
        </Tabs>
      </Card>

      <div className="space-y-3">
        {filtered.length === 0 && (
          <Card className="p-8 text-center text-slate-500 rounded-2xl" data-testid="partners-empty">
            {loading ? "Loading…" : "No facilities match this filter."}
          </Card>
        )}
        {filtered.map((r) => {
          const meta = KIND_META[r.kind];
          const Icon = meta.icon;
          const p = r.partner_user;
          const st = p?.approval_status || "approved";
          return (
            <Card key={`${r.kind}-${r.facility_id}`} className="p-4 sm:p-5 rounded-2xl" data-testid={`dir-row-${r.facility_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3 min-w-[240px] flex-1">
                  <div className={`w-10 h-10 rounded-xl ${meta.bg} ${meta.color} flex items-center justify-center`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px] uppercase">{r.kind}</Badge>
                      <div className="font-display font-bold">{r.facility_name}</div>
                      {r.approval_status !== "approved" && (
                        <Badge className={`${STATUS_COLOR[r.approval_status]} border-0 text-[10px] uppercase`}>{r.approval_status}</Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{r.city}{r.address ? ` · ${r.address}` : ""}</div>
                    {p ? (
                      <div className="mt-2 text-sm">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`${STATUS_COLOR[st]} border-0 text-[10px] uppercase`}>{st}</Badge>
                          <span className="font-semibold">{p.name}</span>
                          <a href={`mailto:${p.email}`} className="text-teal-700">{p.email}</a>
                          {p.phone && <span className="text-slate-500">· {p.phone}</span>}
                          <Badge variant="outline" className="text-[10px] uppercase">portal: {meta.portal}</Badge>
                          {p.must_change_password && <Badge className="bg-amber-100 text-amber-900 border-0 text-[10px] uppercase">temp pw</Badge>}
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-flex items-center gap-1">
                        <ShieldX className="w-3 h-3" /> No partner login yet
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {!p && (
                    <Button size="sm" onClick={() => openInvite(r)} data-testid={`invite-${r.facility_id}`}
                      className="bg-teal-600 hover:bg-teal-500 text-white">
                      <UserPlus className="w-4 h-4 mr-1" /> Invite partner
                    </Button>
                  )}
                  {p && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => resend(r)} data-testid={`resend-${p.id}`}>
                        <Mail className="w-4 h-4 mr-1" /> Resend invite
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => reset(r)} data-testid={`reset-${p.id}`}>
                        <KeyRound className="w-4 h-4 mr-1" /> Reset password
                      </Button>
                      <Select value={st} onValueChange={(v) => setStatus(r, v)}>
                        <SelectTrigger className="w-36" data-testid={`status-${p.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="suspended">Suspended</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    </>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Invite dialog */}
      <Dialog open={!!invite} onOpenChange={(o) => !o && setInvite(null)}>
        <DialogContent className="sm:max-w-md" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite partner for {invite?.facility_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Partner name</Label><Input data-testid="invite-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input data-testid="invite-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="partner@example.com" /></div>
            <div><Label>Phone (optional)</Label><Input data-testid="invite-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 …" /></div>
            <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2">
              A one-time password will be emailed. They can also sign in with Google using the same email.
              Portal on sign-in: <code className="text-teal-700">{invite ? KIND_META[invite.kind].portal : ""}</code>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvite(null)}>Cancel</Button>
            <Button data-testid="invite-submit" onClick={submitInvite} disabled={saving} className="bg-teal-600 hover:bg-teal-500 text-white">
              <ShieldCheck className="w-4 h-4 mr-2" /> {saving ? "Sending…" : "Send invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials dialog (shown after invite/reset so admin can copy if email is delayed) */}
      <Dialog open={!!credsDialog} onOpenChange={(o) => !o && setCredsDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="creds-dialog">
          <DialogHeader>
            <DialogTitle>Sign-in details</DialogTitle>
          </DialogHeader>
          {credsDialog && (
            <div className="space-y-3 text-sm">
              <p className="text-slate-600">Share these with the partner if the email is delayed:</p>
              <Row label="Sign-in URL" value={credsDialog.portal_url} onCopy={() => copy(credsDialog.portal_url)} />
              <Row label="Portal after sign-in" value={KIND_META[credsDialog.kind].portal} />
              <Row label="Email" value={credsDialog.email} onCopy={() => copy(credsDialog.email)} />
              <Row label="One-time password" value={credsDialog.temp_password} onCopy={() => copy(credsDialog.temp_password)} mono />
              <p className="text-xs text-slate-500">Ask them to change the password after first sign-in, or use Google sign-in with the same email.</p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCredsDialog(null)} data-testid="creds-close">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatChip({ label, value, tone }) {
  const t = tone === "emerald" ? "bg-emerald-100 text-emerald-800"
          : tone === "amber" ? "bg-amber-100 text-amber-900"
          : "bg-slate-100 text-slate-700";
  return (
    <div className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${t}`}>
      {label}: <span className="font-mono">{value}</span>
    </div>
  );
}

function Row({ label, value, onCopy, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="flex items-center gap-2">
        <div className={`flex-1 truncate ${mono ? "font-mono text-base" : "text-sm"} bg-slate-50 rounded px-2 py-1`}>{value}</div>
        {onCopy && <Button size="icon" variant="outline" onClick={onCopy}><Copy className="w-3.5 h-3.5" /></Button>}
      </div>
    </div>
  );
}
